import { forwardRef, useCallback, useState } from "react";

// Material UI
import AppBar from "@mui/material/AppBar";
import Icon from "@mui/material/Icon";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { Box, Button } from "@mui/material";
import { store } from "src/redux/store/Store";

// Mosaic Tree helper
import { ILeafNode, IMosaicTabNode } from "src/utils/MosaicTree";

// DragSource, DropTarget,
import { useDrag } from "react-dnd";
import { closeModal, openModal } from "src/redux/actions/App.Actions";
import MosaicTabBarDropTarget from "./MosaicTabBarDropTarget";

// Styling
import classNames from "classnames";
import { useDispatch } from "react-redux";
import { removeTabNode, setActiveIndex } from "src/redux/actions/Mosaic.Actions";
import { Theme } from "@mui/material/styles";

const WrapTabNode = (props: any): JSX.Element => {
  const [collected, drag, dragPreview] = useDrag({ type: "DND_NODE", item: props });
  return <div ref={drag}>{props.children}</div>;
};

interface IMosaicWindowProps {
  leafNode: ILeafNode;
  path: string[];
  splitPercentage?: number;
  classes?: any;
  moveTabNode?: any;
  removeTabNode?: any;
  appendTabNode?: any;
  setActiveIndex?: any;
}

/**
 * Close Mosaic Node - Warning Modal
 * @param tabItem - mosaic window user wanted to close.
 * @param path - path of mosaic window in mosaic tree.
 */
const displayWarningModal = (closeWarning: string, windowTitle: string, closeTabItem: () => void): void => {
  const msg = closeWarning ?? "";
  const title = `Close ${windowTitle}?`;
  store.dispatch(
    openModal({
      root: {
        data: {
          title: title,
          message: msg,
          buttons: [
            {
              label: "Cancel",
              handler: () => {
                store.dispatch(closeModal({ root: { data: null } }));
              },
            },
            {
              label: "Close",
              handler: () => {
                closeTabItem();
                store.dispatch(closeModal({ root: { data: null } }));
              },
            },
          ],
        },
      },
    })
  );
};

const mosaicWindowStyles = (theme: Theme): any => ({
  mosaicWindowRoot: {
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
  },
  mosaicAppbarRoot: {
    backgroundColor: theme.palette.background.default,
    boxShadow: "none",
    zIndex: "auto",
  },
  mosaicTabsRoot: {
    height: theme.custom.mosaicBar.height,
    minHeight: theme.custom.mosaicBar.height,
    overflowY: "auto",
  },
  mosaicTabsFlexContainer: {
    height: theme.custom.mosaicBar.height,
  },
  mosaicTabsIndicator: {
    backgroundColor: theme.palette.secondary.main,
    top: 0,
  },
  mosaicTabRoot: {
    height: theme.custom.mosaicBar.height,
    minHeight: theme.custom.mosaicBar.height,
    "&.active": { backgroundColor: theme.palette.background.paper },
  },
  mosaicTabButtonRoot: {
    height: theme.custom.mosaicBar.height,
    minHeight: theme.custom.mosaicBar.height,
    paddingRight: 0,
    minWidth: "160px",
    "&.active": { opacity: 1 },
  },
  mosaicTabButtonLabelWrapper: {
    "& div": {
      fontSize: "0.875rem",
      lineHeight: "0.875rem",
      width: "100%",
      color: theme.custom.launchpad.iconColor,
      overflow: "hidden",
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
    },
  },
  mosaicTabButtonLabelContainer: {
    padding: 0,
    textAlign: "left",
    lineHeight: "12px",
  },
  mosaicTabCloseRoot: {
    opacity: 0.3,
    margin: "4px",
    padding: "0px",
    minWidth: "26px",
    maxHeight: "26px",
    "&.active": { opacity: 1 },
  },
  selectedTab: {
    backgroundColor: theme.palette.primary.dark,
  },
});

export const MosaicWindow = forwardRef((props: IMosaicWindowProps, ref: any): JSX.Element => {
  const { path, leafNode } = props;

  const tabNodes = leafNode.tabNodes;
  const activeIndex = leafNode.activeIndex;
  const variantConfig = tabNodes.length > 1 ? "scrollable" : "standard";
  const scrollButtons = tabNodes.length > 1 ? "auto" : false;
  const [closeWarningMessage, setCloseWarningMessage] = useState<string>(null);
  const dispatch = useDispatch();

  const handleSetActiveIndex = useCallback(
    (activeIndex: number, path: string[]) => {
      dispatch(setActiveIndex(activeIndex, path));
    },
    [dispatch]
  );

  /** CLose mosaic node item */
  const closeTab = useCallback(
    (tabkey: string, path: string[]): void => {
      dispatch(removeTabNode(path, tabkey));
    },
    [dispatch]
  );

  /**
   * If a mosaic window (node) or one of its children has a close warning, open a confirmation modal displaying warning.
   * @param tabItem -
   * @param path -
   */
  const handleClosetab = useCallback(
    (tabItem: IMosaicTabNode, path: string[]): void => {
      if (closeWarningMessage && tabItem.componentName != "PairingSummaryWindow") {
        displayWarningModal(closeWarningMessage, tabItem.title, () => {
          closeTab(tabItem.id, path);
        });
      } else {
        closeTab(tabItem.id, path);
      }
    },
    [closeTab, closeWarningMessage]
  );

  return (
    <Box sx={(theme) => mosaicWindowStyles(theme).mosaicWindowRoot}>
      <AppBar sx={(theme) => mosaicWindowStyles(theme).mosaicAppbarRoot} position="static" enableColorOnDark>
        <Tabs
          sx={(theme) => ({
            "&.MuiTabs-root": mosaicWindowStyles(theme).mosaicTabsRoot,
            "&.MuiTabs-flexContainer": mosaicWindowStyles(theme).mosaicTabsFlexContainer,
            "&.MuiTabs-indicator": mosaicWindowStyles(theme).mosaicTabsIndicator,
          })}
          value={activeIndex >= 0 ? activeIndex : false}
          variant={variantConfig}
          scrollButtons={scrollButtons}
        >
          {tabNodes.map((tabItem: IMosaicTabNode, index: number) => {
            const activeTab = index === activeIndex ? true : false;
            tabItem.setCloseWarning = setCloseWarningMessage; // bind the setCloseWarning function to the tabItem

            return (
              <WrapTabNode key={`${tabItem.id}-dragSource`} mosaicTabNode={tabItem} path={path}>
                <Box
                  className={classNames(activeTab && "active")}
                  sx={(theme) => mosaicWindowStyles(theme).mosaicTabRoot}
                >
                  <Tab
                    wrapped={true}
                    className={classNames(activeTab && "active")}
                    sx={(theme) => ({
                      "&.MuiTab-root": mosaicWindowStyles(theme).mosaicTabButtonRoot,
                      "&.MuiTab-wrapped": mosaicWindowStyles(theme).mosaicTabButtonLabelWrapper,
                    })}
                    disableRipple={true}
                    onClick={() => handleSetActiveIndex(index, path)}
                    value={index}
                    label={
                      <Tooltip title={tabItem.title} enterDelay={1000} placement="top">
                        <div>{tabItem.title}</div>
                      </Tooltip>
                    }
                  />
                  <Button
                    color="inherit"
                    id={`close_mosaic#${tabItem.id}_btn`}
                    className={classNames(activeTab && "active")}
                    sx={(theme) => mosaicWindowStyles(theme).mosaicTabCloseRoot}
                    onClick={(event) => {
                      event.preventDefault();
                      handleClosetab(tabItem, path);
                    }}
                  >
                    <Icon>close</Icon>
                  </Button>
                </Box>
              </WrapTabNode>
            );
          })}
          <MosaicTabBarDropTarget path={path} />
        </Tabs>
      </AppBar>
      {tabNodes.map((tabItem: IMosaicTabNode, index: number) => {
        return (
          <div
            key={tabItem.id}
            id={tabItem.id + "-reparentable"}
            style={{
              display: index === activeIndex ? "flex" : "none",
              flexGrow: 1,
              minHeight: 0,
              minWidth: 0,
              flexBasis: 0,
            }}
          />
        );
      })}
    </Box>
  );
});
